import { Box, CircularProgress, Link, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import {
  RichTreeViewPro,
  TreeItem2Checkbox,
  TreeItem2Content,
  TreeItem2DragAndDropOverlay,
  TreeItem2GroupTransition,
  TreeItem2Icon,
  TreeItem2IconContainer,
  TreeItem2Provider,
  TreeItem2Root,
  TreeViewBaseItem,
  useTreeItem2,
  UseTreeItem2Parameters,
  UseTreeItem2Status,
  useTreeViewApiRef,
} from "@mui/x-tree-view-pro";
import React, { use, useEffect } from "react";
import { Link as RouterLink } from "react-router";
import { thumbnailService } from "../../../Core/Api/services";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import {
  fetchAndStoreChildrenInTreeById,
  fetchInitialTree,
  GetExpandedItems,
  GetSelectedItems,
  GetTreeObj,
  GetTreeStatus,
  SetExpandedItems,
  SetSelectedItems,
  TreeContext,
} from "../../../Redux/Reducers/redux-slice-tree";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { routePaths } from "../../../Router/router";
import useHover from "../../../Utils/Hooks/useHover";
import { DriveContext } from "../../Drive/Common/drive-navigate-handler";
import AssetIcon from "../../Misc/AssetIcon/asset-icon";
import PosterImage from "../../Misc/PosterImage";
import { IntersectArray, UserHasAcls } from "../../../Utils/helpers";
import RotatableBox from "../../Access/Shared/rotatable-box";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { debounce } from "throttle-debounce";
import useDraggable from "../../../Utils/Hooks/useDraggable";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { GetDraggingIds } from "../../../Redux/Reducers/redux-slice-layout";

interface AssetTreeViewProps {
  rootObjectId: number;
  context: TreeContext;
  currentAssetId?: number;
  // searchTerm?: string;
  multiselect?: boolean;
  disableSelection?: boolean;
  disableNavigation?: boolean;
  unselectableIds?: number[];
  hilightedObjectId?: number;
  hideRoot?: boolean;
  searchTerm?: string;
}

type AssetTreeViewItemType = {
  id: string;
  label: string;
  childCount: number;
  children?: AssetTreeViewItem[];
  layer: ObjectLayer;
  type: ObjectType;
};

type AssetTreeViewItem = TreeViewBaseItem<AssetTreeViewItemType>;

function mapGraphToTree(
  graph: DbAssetObjectInfos,
  // objectClassesToExclude: ObjectClass[],
  // objectTypesToExclude: ObjectType[],
  unselectableIds: number[],
  hilightedObjectId?: number
): AssetTreeViewItem {
  const children = (graph.childObjects ?? []).map((x) => mapGraphToTree(x, unselectableIds, hilightedObjectId)) || [];

  const childCount = children.length || graph.childCount || 0;

  switch (graph.layer) {
    case ObjectLayer.Title:
      children.sort((a, b) => a.label.localeCompare(b.label));
      break;
    default:
      children.sort(
        (a, b) =>
          a.type === ObjectType.Folder && b.type === ObjectType.Folder
            ? a.label.localeCompare(b.label) // Sort folders by name
            : Number(a.id) - Number(b.id) // Sort other assets by id
      );
  }

  return {
    id: String(graph.id),
    label: graph.name,
    childCount: childCount,
    children: children,
    layer: graph.layer,
    type: graph.type,
  };
}

function getItemDescendantsIds(item: AssetTreeViewItem): number[] {
  const ids: number[] = [];

  if (item && item.children) {
    item.children.forEach((child) => {
      ids.push(Number(child.id));
      ids.push(...getItemDescendantsIds(child));
    });
  }

  return ids;
}

function getItemParentIds(itemId: number, tree: AssetTreeViewItem): number[] {
  const parentIds: number[] = [];

  function findParentIds(currentItem: AssetTreeViewItem) {
    if (currentItem.id === String(itemId)) {
      return true;
    }

    for (const child of currentItem.children || []) {
      if (findParentIds(child)) {
        parentIds.push(Number(currentItem.id));
        return true;
      }
    }

    return false;
  }

  findParentIds(tree);

  return parentIds;
}

function filterTreeItems(tree: AssetTreeViewItem, searchTerm: string): AssetTreeViewItem | null {
  if (!tree) return null;

  const matches = tree.label.includesAllWords(searchTerm) || tree.id.includesAllWords(searchTerm);

  const filteredChildren = (tree.children || [])
    .map((child) => filterTreeItems(child, searchTerm))
    .filter((child) => child !== null) as AssetTreeViewItem[];

  if (matches || filteredChildren.length > 0) {
    return {
      ...tree,
      children: filteredChildren,
    };
  }

  return null;
}

interface TreeviewContextType {
  context: TreeContext;
  disableNavigation: boolean;
  isItemSelectable: (itemId: number) => boolean;
  isItemHilighted: (itemId: number) => boolean;
  isItemDisabled: (itemId: number) => boolean;
}

const TreeviewContext = React.createContext<TreeviewContextType>({
  context: TreeContext.TitlePanel,
  disableNavigation: false,
  isItemSelectable: (itemId: number) => false,
  isItemHilighted: (itemId: number) => false,
  isItemDisabled: (itemId: number) => false,
});

const AssetTreeView = (props: AssetTreeViewProps) => {
  const {
    rootObjectId,
    currentAssetId = undefined,
    context,
    unselectableIds: props_unselectableIds = [],
    disableSelection = false,
    multiselect = false,
    disableNavigation = false,
    hideRoot = false,
    searchTerm = "",
  } = props;

  const dispatch = useAppDispatch();
  const apiRef = useTreeViewApiRef();

  const treeObj = useAppSelector(GetTreeObj(context));
  const treeStatus = useAppSelector(GetTreeStatus(context));
  const expandedItems = useAppSelector(GetExpandedItems(context));
  const selectedItems = useAppSelector(GetSelectedItems(context));

  const [localUnselectableIds, setLocalUnselectableIds] = React.useState<number[]>([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState(searchTerm);

  const unselectableIds = React.useMemo(() => {
    return [...new Set([...props_unselectableIds, ...localUnselectableIds])];
  }, [props_unselectableIds, localUnselectableIds]);

  const handleSearchTermChange = React.useCallback(
    debounce(300, (term: string) => {
      // 300ms debounce delay
      setDebouncedSearchTerm(term);
    }),
    []
  );

  const treeItems = React.useMemo(() => {
    if (!treeObj) return null;

    const fullTree = mapGraphToTree(treeObj, unselectableIds, currentAssetId);

    if (debouncedSearchTerm) {
      return filterTreeItems(fullTree, debouncedSearchTerm);
    }

    return fullTree;
  }, [treeObj, unselectableIds, currentAssetId, debouncedSearchTerm]);

  const items = React.useMemo(() => {
    if (!treeItems) return [];

    if (hideRoot) {
      if (!treeItems.children) return [];
      return treeItems.children as AssetTreeViewItem[];
    }

    // If hideRoot is false, we return the root item itself
    return [treeItems];
  }, [treeItems, hideRoot]);

  const handleSelectedItemsChange = (event: React.SyntheticEvent, itemIds: string[] | null) => {
    if (!itemIds) return;

    // remove all other items but the first from selection
    // handled manually cause MUI changes signature of both selectedItems and onSelectedItemsChange which cause weird behaviors
    if (!multiselect) {
      itemIds = [itemIds[0]];
    }

    let selectedItems: string[] = [];
    selectedItems = itemIds.filter((itemId) => itemId !== null);

    const unselectableIds: number[] = [];

    for (const itemId of selectedItems) {
      const item = apiRef.current!.getItem(itemId) as AssetTreeViewItem;
      const itemDescendantsIds = getItemDescendantsIds(item);

      unselectableIds.push(...itemDescendantsIds);
    }

    selectedItems = selectedItems.filter((id) => !unselectableIds.includes(Number(id)));
    setLocalUnselectableIds(unselectableIds);

    dispatch(SetSelectedItems({ context: context, selectedIds: selectedItems.map((x) => Number(x)), replace: true }));
  };

  const handleExpandedItemsChange = (event: React.SyntheticEvent, itemIds: string[]) => {
    dispatch(SetExpandedItems({ context: context, expandedIds: itemIds.map((x) => Number(x)), replace: true }));
  };

  const isItemSelectable = React.useCallback(
    (itemId: number) => {
      if (!treeItems) return false;

      if (disableSelection) return false;

      if (!selectedItems) return true;

      if (unselectableIds.includes(itemId)) return false;

      // Check if the item has any selected parent
      const parentIds = getItemParentIds(itemId, treeItems);

      return IntersectArray(parentIds, selectedItems.map(Number)).length === 0;
    },
    [treeItems, disableSelection, selectedItems, unselectableIds]
  );

  const isItemDisabled = React.useCallback(
    (itemId: number) => {
      if (!treeItems) return true;
      // Selection is enabled but the item is not selectable
      if (!disableSelection && !isItemSelectable(itemId)) return true;

      return false;
    },
    [treeItems]
  );

  const isItemHilighted = React.useCallback(
    (itemId: number) => {
      return currentAssetId === itemId;
    },
    [currentAssetId]
  );

  const treeViewContext = React.useMemo(() => {
    return {
      context: context,
      disableNavigation: disableNavigation,
      isItemSelectable: isItemSelectable,
      isItemHilighted: isItemHilighted,
      isItemDisabled: isItemDisabled,
    };
  }, [context, disableNavigation, isItemSelectable, isItemHilighted]);

  // Fetch initial tree object
  React.useEffect(() => {
    if (!treeObj || treeObj.id !== rootObjectId)
      dispatch(fetchInitialTree({ rootId: rootObjectId, currentId: currentAssetId, context: context }));
  }, [treeObj, rootObjectId, context, currentAssetId]);

  React.useEffect(() => {
    handleSearchTermChange(searchTerm);
  }, [searchTerm]);

  if (treeStatus === "pending") return <TreeviewSkeleton />;

  if (treeItems === null) return <></>;

  return (
    <>
      {/* TODO : Try to use easier way (from MUI doc) to select node children*/}
      <TreeviewContext value={treeViewContext}>
        <RichTreeViewPro
          apiRef={apiRef}
          items={items}
          // Selection
          checkboxSelection={!disableSelection}
          disableSelection={disableSelection}
          // we handle this manually cause MUI changes signatures which cause weird behaviors
          multiSelect={true}
          selectedItems={selectedItems.map((x) => String(x))}
          onSelectedItemsChange={handleSelectedItemsChange}
          // Expansion
          expandedItems={expandedItems.map((x) => String(x))}
          onExpandedItemsChange={handleExpandedItemsChange}
          // Customization
          slots={{ item: CustomTreeItem }}
          // Params
          //expansionTrigger="iconContainer"
        />
      </TreeviewContext>
    </>
  );
};

export default AssetTreeView;

interface CustomTreeItemProps
  extends Omit<UseTreeItem2Parameters, "rootRef">,
    Omit<React.HTMLAttributes<HTMLLIElement>, "onFocus"> {}

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
  props: CustomTreeItemProps,
  treeRef: React.Ref<HTMLLIElement>
) {
  const { id, itemId, label, disabled, children, ...other } = props;

  const [loading, setLoading] = React.useState(false);
  const treeViewContext = use(TreeviewContext);
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const {
    getRootProps,
    getContentProps,
    getCheckboxProps,
    getGroupTransitionProps,
    getDragAndDropOverlayProps,
    status: _status,
    publicAPI,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: treeRef });

  //   const { interactions } = useTreeItem2Utils({
  //     itemId: props.itemId,
  //     children: props.children,
  //   });

  const item = publicAPI.getItem(itemId) as AssetTreeViewItem;

  const status: UseTreeItem2Status = React.useMemo(() => {
    return {
      ..._status,
      expandable: item.childCount > 0,
    };
  }, [_status, item.childCount]);

  const hilightItem: boolean = React.useMemo(() => {
    return treeViewContext.isItemHilighted(Number(itemId));
  }, [treeViewContext.isItemHilighted, itemId]);

  const isItemSelectable: boolean = React.useMemo(() => {
    return treeViewContext.isItemSelectable(Number(itemId));
  }, [treeViewContext.isItemSelectable, itemId]);

  const isItemDisabled: boolean = React.useMemo(() => {
    return treeViewContext.isItemDisabled(Number(props.id));
  }, [treeViewContext.isItemDisabled, props.id]);

  const handleOnClickContainer = React.useCallback(
    async (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();

      if (status.expandable) {
        // Check if the item has a childCount > 0 but no children loaded yet
        if (!status.expanded && item.childCount > 0 && (!item.children || item.children.length === 0)) {
          setLoading(true);
          await dispatch(fetchAndStoreChildrenInTreeById({ id: Number(itemId), context: treeViewContext.context }));
          setLoading(false);
        }

        publicAPI.setItemExpansion(event, itemId, !status.expanded);
      }
    },
    [status, publicAPI, itemId]
  );

  const handleOnClickCheckBox = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();

      publicAPI.selectItem({ event, itemId, keepExistingSelection: true });
    },
    [publicAPI, itemId]
  );

  const draggingIds = useAppSelector(GetDraggingIds);

  //Drag & Drop
  const aclMove = UserHasAcls([AclTypes.aclMetadataMoveLinkContentUpdate, AclTypes.aclElevationWishlistCreate]); // aclMetadataMoveLinkTitleUpdate not needed here as we stay on same title
  const { isDragging, isDragOver, state, ref } = useDraggable(
    ObjectType.Folder,
    parseInt(itemId),
    false,
    "card",
    aclMove ? "drop" : "none",
    draggingIds,
    TreeContext.DriveTreeview
  );

  const hovered = useHover(ref);

  return (
    <TreeItem2Provider itemId={itemId}>
      <TreeItem2Root
        {...getRootProps(other)}
        sx={{
          background: "transparent",
          border: "1px solid",
          borderColor: "transparent",
          ...(status.selected && {
            background: theme.palette.primary.alpha,
            borderColor: theme.palette.primary.main,
          }),
          borderRadius: "6px",
        }}
      >
        <TreeItem2Content
          {...getContentProps({
            sx: {
              background: hilightItem ? theme.palette.primary.alpha : "transparent",
              backgroundImage: isDragOver
                ? `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='${theme.palette.primary.main.replace(
                    "#",
                    "%23"
                  )}' stroke-width='3' stroke-dasharray='4%2c 8' stroke-dashoffset='21' stroke-linecap='round'/%3e%3c/svg%3e")`
                : "",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              flex: "1 1 0",
              minWidth: 0,
              py: 0.5,
              pr: 0.75,
            },
          })}
          ref={ref}
          onClick={handleOnClickContainer}
        >
          <TreeItem2IconContainer
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "28px" }}
          >
            {loading ? (
              <CircularProgress size={16} />
            ) : (
              <RotatableBox from={-90} to={0} rotated={status.expanded}>
                <TreeItem2Icon status={status} slots={{ expandIcon: ExpandMoreOutlined }} />
              </RotatableBox>
            )}
          </TreeItem2IconContainer>
          <CustomLabel
            id={item.id}
            label={item.label}
            childCount={item.childCount}
            layer={item.layer}
            type={item.type}
            disableNavigation={treeViewContext.disableNavigation}
          />
          {(hovered || status.selected) && isItemSelectable && !isItemDisabled && (
            <Stack sx={{ height: "28px", alignItems: "center", justifyContent: "center" }}>
              <TreeItem2Checkbox {...getCheckboxProps()} onClick={handleOnClickCheckBox} />
            </Stack>
          )}
          <TreeItem2DragAndDropOverlay {...getDragAndDropOverlayProps()} />
        </TreeItem2Content>
        {(children || item.childCount > 0) && <TreeItem2GroupTransition {...getGroupTransitionProps()} />}
      </TreeItem2Root>
    </TreeItem2Provider>
  );
});

interface CustomLabelProps {
  id: string;
  label: string;
  childCount: number;
  layer: ObjectLayer;
  type: ObjectType;
  disableNavigation?: boolean;
}

function CustomLabel(props: CustomLabelProps) {
  const LabelContent = React.useMemo(() => {
    switch (props.layer) {
      case ObjectLayer.Title:
        return <TitleLabelContent {...props} />;
      case ObjectLayer.Asset:
      case ObjectLayer.Clip:
        return <AssetLabelContent {...props} />;
      case ObjectLayer.Content:
      case ObjectLayer.SubContent:
      case ObjectLayer.Head:
      case ObjectLayer.Group:
        return <GroupLabelContent {...props} />;
      default:
        return null;
    }
  }, []);

  const treeViewContext = use(TreeviewContext);

  const isItemDisabled: boolean = React.useMemo(() => {
    return treeViewContext.isItemDisabled(Number(props.id));
  }, [treeViewContext.isItemDisabled, props.id]);

  return (
    <Stack
      direction="row"
      gap={1}
      sx={{
        alignItems: "flex-start",
        flex: "1 1 0",
        minWidth: 0,
        py: 0.5,
        pr: 0.5,
        ...(isItemDisabled && { pointerEvents: "none", opacity: 0.5 }),
      }}
    >
      {LabelContent}
    </Stack>
  );
}

function TitleLabelContent(props: CustomLabelProps) {
  const { id, label, disableNavigation } = props;

  return (
    <>
      <PosterImage
        width={32}
        height={48}
        borderRadius="2px"
        flexShrink={0}
        img={{
          src: thumbnailService.GetObjectPosterUrl(Number(id), ThumbnailSize.small),
        }}
      />
      <LabelContentLink text={label} id={id} disableNavigation={disableNavigation} variant="title" />
    </>
  );
}

function GroupLabelContent(props: CustomLabelProps) {
  const { id, label, layer, type, disableNavigation, childCount } = props;

  const localLabel = React.useMemo(() => {
    switch (layer) {
      case ObjectLayer.Content:
        return `Content ${label}`;
      default:
        return label;
    }
  }, [label, layer]);

  return (
    <>
      <Stack sx={{ height: "20px", justifyContent: "center", alignItems: "center" }}>
        <AssetIcon type={type} colored />
      </Stack>
      <LabelContentLink text={localLabel} id={id} disableNavigation={disableNavigation} variant="asset" />
      <Typography className="tree-row-title" color="text.tier">
        {childCount.toString()}
      </Typography>
    </>
  );
}

function AssetLabelContent(props: CustomLabelProps) {
  const { id, label, type, disableNavigation, childCount } = props;

  const treeViewContext = React.use(TreeviewContext);

  return (
    <>
      <Stack sx={{ height: "20px", justifyContent: "center", alignItems: "center" }}>
        <AssetIcon type={type} colored />
      </Stack>
      <Stack gap={0.5} sx={{ flex: "1 1 0", minWidth: 0, overflow: "hidden" }}>
        {treeViewContext.context !== TreeContext.AssetTarPreview ? (
          <>
            <LabelContentLink text={`#${id}`} id={id} disableNavigation={disableNavigation} variant="asset" />
            <Typography color="text.tier">{label}</Typography>
          </>
        ) : (
          <Typography>{label}</Typography>
        )}
      </Stack>
      {childCount > 0 && (
        <Typography className="tree-row-title" color="text.tier">
          {childCount.toString()}
        </Typography>
      )}
    </>
  );
}

function LabelContentLink(props: {
  text: string;
  id: string;
  variant: "title" | "asset";
  disableNavigation?: boolean;
}) {
  const { text, id, disableNavigation, variant } = props;
  const driveContext = React.use(DriveContext);
  const treeViewContext = React.use(TreeviewContext);

  const url = React.useMemo(() => {
    if ([TreeContext.DriveTreeview, TreeContext.MoveDrive].includes(treeViewContext.context)) {
      if (driveContext?.basePath) return `${driveContext.basePath}/${id}`;
      return `${routePaths.drive.path}/${id}`;
    } else {
      if (variant === "title") return `${routePaths.title.path}/${id}`;
      else return `${routePaths.asset.path}/${id}`;
    }
  }, [treeViewContext.context, id]);

  if (disableNavigation) {
    return (
      <Box sx={(t) => ({ flex: "1 1 0" })}>
        <Typography color="text.primary" fontWeight={500} fontSize="body1">
          {text}
        </Typography>
      </Box>
    );
  } else {
    return (
      <Box sx={(t) => ({ flex: "1 1 0" })}>
        <Link
          component={RouterLink}
          color="text.primary"
          fontWeight={variant === "title" ? 600 : 500}
          variant={variant === "title" ? "subtitle2" : "body1"}
          underline="hover"
          to={url}
          onClick={(e) => e.stopPropagation()}
        >
          {text}
        </Link>
      </Box>
    );
  }
}

const TreeviewSkeleton = () => {
  return (
    <Stack width="100%">
      {[...Array(3)].map((_, index) => (
        <Stack
          key={index}
          gap={1}
          sx={{
            mb: 2,
          }}
        >
          <Stack direction="row" gap={1}>
            <Skeleton variant="rounded" width={24} height={28} />
            <Skeleton variant="text" sx={(t) => ({ flex: "1 1 0", ...t.typography.subtitle2 })} />
          </Stack>
          <Stack direction="row" pl={2} gap={1}>
            <Skeleton variant="rounded" width={24} height={24} />
            <Skeleton variant="text" sx={(t) => ({ flex: "1 1 0", ...t.typography.body1 })} />
          </Stack>
          <Stack direction="row" pl={2} gap={1}>
            <Skeleton variant="rounded" width={24} height={24} />
            <Skeleton variant="text" sx={(t) => ({ flex: "1 1 0", ...t.typography.body1 })} />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};
